import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useMetrics } from '../../../hooks/useMetrics';
import { AUTH_EVENTS, AuthFunnelMode } from '../../../utils/authFunnel';
import HopLoader from '../../_atoms/HopLoader/HopLoader';
import { BackIco } from './AuthIcons';
import {
  CenterWrap,
  CodeBoxes,
  ConfirmActions,
  LoadingOverlay,
  OAuthBtn,
  PrimaryBtn,
  Sticker,
  SupportRef,
} from './authStyles';
import { AuthStrings } from './useAuthStrings';

type ResendResult = { status: 'ok' | 'rate-limited' | 'error'; correlationId?: string };

type Props = {
  t: AuthStrings;
  email: string;
  /** login|signup, for funnel events. */
  mode: AuthFunnelMode;
  /** Verify the 6-digit code with the backend. Resolves on success. */
  onVerify: (code: string) => Promise<void>;
  /** Re-request a code for the same email. Resolves with the outcome. */
  onResend: () => Promise<ResendResult>;
  /** Switch to a different provider (back to the options screen). */
  onTryAnother: () => void;
  /**
   * Error to show on first render — used when the in-app-browser navigation flow
   * bounces back here after a failed code, so the message survives the full-page
   * reload (component state doesn't).
   */
  initialError?: string;
};

/**
 * Inline magic-link second screen: paste/type the 6-digit code emailed to the
 * user. Verification is delegated to `onVerify` (backend-owned, CURI-334).
 */
const MagicLinkCodeConfirm: React.FC<Props> = ({
  t,
  email,
  mode,
  onVerify,
  onResend,
  onTryAnother,
  initialError,
}) => {
  const metrics = useMetrics(true);
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [resent, setResent] = useState(false);
  const [err, setErr] = useState('');
  const [errRef, setErrRef] = useState<string | undefined>();
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const resendTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  // Clear the resend cooldown timer if the component unmounts.
  useEffect(() => () => clearTimeout(resendTimer.current), []);

  // Editing the code clears any prior error (e.g. an "enter the code" nudge or a
  // failed-verify message) so it doesn't linger while the user corrects it.
  useEffect(() => {
    setErr('');
    setErrRef(undefined);
  }, [digits]);
  // Seed the failed-verify message when we arrive here via the in-app-browser
  // navigation flow (full reload, so component state is fresh). Declared after the
  // [digits] reset above so it wins on mount; typing then clears it. Runs once.
  useEffect(() => {
    if (initialError) setErr(initialError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resend = async () => {
    if (resent) return;
    setErr('');
    setErrRef(undefined);
    metrics.logEvent(AUTH_EVENTS.codeResent, { mode });
    // Optimistically disable to prevent double-clicks while the request is in flight.
    setResent(true);
    const { status, correlationId } = await onResend();
    if (status !== 'ok') {
      // Re-enable immediately and surface why no code was sent (+ support ref).
      setResent(false);
      metrics.logEvent(AUTH_EVENTS.sendFailed, { mode, reason: status, resend: true });
      setErr(status === 'rate-limited' ? t.errTooManyRequests : t.errSendCodeFailed);
      if (status === 'error') setErrRef(correlationId);
      return;
    }
    // Re-enable after a short cooldown so a missed/spam-filtered code is recoverable.
    resendTimer.current = setTimeout(() => setResent(false), 30000);
  };

  // Spread a multi-character string across the boxes starting at `startIdx`, then
  // focus the last box it reached. Shared by paste and by autofill (see `set`).
  const fillFrom = (startIdx: number, text: string) => {
    const clean = text.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (!clean) return;
    setDigits(prev => {
      const n = [...prev];
      for (let k = 0; k < clean.length && startIdx + k < 6; k += 1) {
        n[startIdx + k] = clean[k];
      }
      return n;
    });
    refs.current[Math.min(startIdx + clean.length - 1, 5)]?.focus();
  };

  const set = (idx: number, val: string) => {
    // Codes are alphanumeric and forced uppercase.
    const clean = val.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    // iOS QuickType autofill drops the whole code into the single focused box
    // (and can exceed a maxLength) rather than distributing it — so any value
    // longer than one char is spread across the remaining boxes from here.
    if (clean.length > 1) {
      fillFrom(idx, clean);
      return;
    }
    setDigits(d => {
      const n = [...d];
      n[idx] = clean;
      return n;
    });
    if (clean && idx < 5) refs.current[idx + 1]?.focus();
  };

  // Regular typing is handled here (not via `set`/onChange) so we control
  // insert/backspace/arrow-nav semantics ourselves rather than depending on
  // each box's native maxLength-1 replace-selection behavior, which is what
  // caused a keystroke to get silently swallowed when retyping over an
  // already-filled box. `set`/onChange remains the fallback path for paste
  // and one-time-code autofill, which don't fire per-character keydowns.
  const onKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    if (/^[a-zA-Z0-9]$/.test(e.key)) {
      e.preventDefault();
      const char = e.key.toUpperCase();
      // Insert at the highlighted box and shift everything after it right,
      // dropping the last character off the end — not an in-place overwrite.
      setDigits(prev => {
        const n = [...prev];
        for (let i = 5; i > idx; i -= 1) n[i] = n[i - 1];
        n[idx] = char;
        return n;
      });
      refs.current[Math.min(idx + 1, 5)]?.focus();
      return;
    }

    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[idx]) {
        setDigits(prev => {
          const n = [...prev];
          n[idx] = '';
          return n;
        });
      } else if (idx > 0) {
        setDigits(prev => {
          const n = [...prev];
          n[idx - 1] = '';
          return n;
        });
        refs.current[idx - 1]?.focus();
      }
      return;
    }

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (idx > 0) refs.current[idx - 1]?.focus();
      return;
    }

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      if (idx < 5) refs.current[idx + 1]?.focus();
    }
  };

  // Whichever box currently has focus should always show its character
  // highlighted so the next keystroke's effect is obvious — re-select after
  // every digits change (not just on focus) since a shift can change what's
  // in the focused box without moving focus away from it.
  useEffect(() => {
    const idx = refs.current.findIndex(r => r === document.activeElement);
    refs.current[idx]?.select();
  }, [digits]);

  const onPaste = (e: React.ClipboardEvent) => {
    const txt = e.clipboardData.getData('text') || '';
    if (!/[a-zA-Z0-9]/.test(txt)) return;
    e.preventDefault();
    fillFrom(0, txt);
  };

  const full = digits.every(d => d !== '');

  const submit = async () => {
    if (verifying) return;
    if (!full) {
      setErr(t.enterCode);
      setErrRef(undefined);
      return;
    }
    setErr('');
    setErrRef(undefined);
    metrics.logEvent(AUTH_EVENTS.codeSubmitted, { mode });
    setVerifying(true);
    try {
      await onVerify(digits.join(''));
    } catch (e) {
      metrics.logEvent(AUTH_EVENTS.codeFailed, { mode });
      setErr(e instanceof Error ? e.message : t.errCodeInvalid);
      setVerifying(false);
    }
  };

  // While verifying, cover the logo + card with a full-screen loader only —
  // this reads as the app already loading and hands off cleanly to MainLoader.
  // Portaled to <body> so it escapes the card's stacking context and also
  // covers the corner language switcher.
  if (verifying && !err) {
    const overlay = (
      <LoadingOverlay>
        <HopLoader label={t.signingIn} />
      </LoadingOverlay>
    );
    return typeof document !== 'undefined'
      ? createPortal(overlay, document.body)
      : overlay;
  }

  return (
    <CenterWrap>
      <Sticker src="/auth/stickers/love.svg" alt="" style={{ width: 104, height: 104 }} />
      <h2>{t.magicTitle}</h2>
      <p>
        {t.codeSub} <span className="email-em">{email}</span>.
      </p>
      <form
        noValidate
        onSubmit={e => {
          e.preventDefault();
          submit();
        }}
      >
        <CodeBoxes
          onPaste={onPaste}
          onClick={e => {
            if (e.metaKey || e.ctrlKey) {
              setDigits(['', '', '', '', '', '']);
              refs.current[0]?.focus();
            }
          }}
        >
          {digits.map((d, idx) => (
            <input
              key={idx}
              ref={el => (refs.current[idx] = el)}
              value={d}
              inputMode="text"
              autoComplete={idx === 0 ? 'one-time-code' : 'off'}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              style={{ textTransform: 'uppercase' }}
              className={d ? 'filled' : ''}
              onChange={e => set(idx, e.target.value)}
              onKeyDown={e => onKeyDown(idx, e)}
              onFocus={e => e.target.select()}
              aria-label={`${t.codeCharLabel} ${idx + 1}`}
              aria-invalid={!!err}
              aria-describedby={err ? 'code-error' : undefined}
            />
          ))}
        </CodeBoxes>
        {err && (
          <p
            id="code-error"
            role="alert"
            style={{ color: '#D84C42', fontSize: 13, fontWeight: 700 }}
          >
            {err}
          </p>
        )}
        {errRef && (
          <SupportRef>
            {t.errSupportRef}: <code>{errRef}</code>
          </SupportRef>
        )}
        <div
          style={{
            marginTop: 18,
            fontSize: 14,
            fontWeight: 700,
            color: 'rgba(40,47,51,.5)',
          }}
        >
          {t.didntGet}{' '}
          <button
            type="button"
            onClick={resend}
            disabled={resent}
            style={{
              color: resent ? 'rgba(40,47,51,.38)' : '#E58332',
              fontWeight: 800,
              textDecoration: resent ? 'none' : 'underline',
              textUnderlineOffset: 2,
              background: 'none',
              border: 'none',
              cursor: resent ? 'default' : 'pointer',
              fontSize: 'inherit',
              fontFamily: 'inherit',
            }}
          >
            {resent ? t.codeResent : t.sendNew}
          </button>
        </div>
        <PrimaryBtn type="submit" style={{ marginTop: 24 }}>
          {t.continue}
        </PrimaryBtn>
      </form>
      <ConfirmActions>
        <OAuthBtn type="button" onClick={onTryAnother}>
          <BackIco />
          {t.tryAnother}
        </OAuthBtn>
      </ConfirmActions>
    </CenterWrap>
  );
};

export default MagicLinkCodeConfirm;
